import type { AxiosRequestConfig } from 'axios'
import axios, { AxiosError, type AxiosInstance } from 'axios'
import Cookies from 'js-cookie'

import { requestLoginRedirect } from '@/lib/auth-redirect'
import { RefreshAttemptGuard } from '@/lib/auth/refresh-attempt-guard'
import { shouldForceLogoutAfterRefreshError } from '@/lib/auth/refresh-pipeline-error'
import { withRefreshTokenLock } from '@/lib/auth/refresh-token-lock'
import { COOKIE_KEYS } from '@/lib/configs/auth-config'
import store from '@/lib/configs/store-config'
import { isRunningStaleBuild } from '@/lib/utils/check-build-freshness'
import { decodeJwtPayload } from '@/lib/utils/jwt'
import { authAtom, clearAuthState, updateAuthTokens } from '@/state/auth'
import { connectionStatusAtom, markConnectionDegraded } from '@/state/connection'
import { AuthState } from '@/types/auth'

interface QueueItem {
  resolve: (value: string | null) => void
  reject: (error: unknown) => void
}

class ServiceUtil {
  private static instance: ServiceUtil | null = null
  private isRefreshing = false
  private failedQueue: Array<QueueItem> = []
  private refreshRetryAfter = 0
  private lastRefreshError: unknown
  private refreshGuard?: RefreshAttemptGuard

  private constructor() {}

  static getInstance(): ServiceUtil {
    if (!ServiceUtil.instance) {
      ServiceUtil.instance = new ServiceUtil()
    }
    return ServiceUtil.instance
  }

  private isUnauthorizedError = (error: unknown): error is AxiosError => {
    return axios.isAxiosError(error) && error.response?.status === 401
  }

  private isTooManyRequestsError = (error: unknown): error is AxiosError => {
    return axios.isAxiosError(error) && error.response?.status === 429
  }

  private lastRateLimitToastTime = 0

  private showRateLimitToast = () => {
    const now = Date.now()
    if (now - this.lastRateLimitToastTime < 10_000) return
    this.lastRateLimitToastTime = now
    if (typeof window !== 'undefined') {
      import('@/lib/toast').then(({ toast }) => {
        toast.warning('Rate limit reached. Retrying automatically…')
      })
    }
  }

  private showRateLimitGaveUpToast = () => {
    if (typeof window !== 'undefined') {
      import('@/lib/toast').then(({ toast }) => {
        toast.error('Still rate limited after several retries — please try again shortly.')
      })
    }
  }

  // Uncapped retries here previously meant a request stuck behind a 429 that
  // never clears (e.g. a broken/misconfigured limit on the backend) retried
  // forever, showing the rate-limit toast on a loop with no way out short of
  // reloading the page.
  private static readonly MAX_429_RETRIES = 6

  private handle429Retry = async (
    error: AxiosError,
    client: AxiosInstance,
    originalRequest: AxiosRequestConfig
  ): Promise<any> => {
    const retryCount: number = originalRequest._retryCount429 ?? 0

    if (retryCount >= ServiceUtil.MAX_429_RETRIES) {
      this.showRateLimitGaveUpToast()
      return Promise.reject(error)
    }

    this.showRateLimitToast()

    const retryAfterHeader = (error.response?.headers as Record<string, string>)?.['retry-after']
    let delayMs: number

    if (retryAfterHeader) {
      const seconds = parseInt(retryAfterHeader, 10)
      delayMs = isNaN(seconds) ? Math.pow(2, retryCount) * 1000 : seconds * 1000
    } else {
      delayMs = Math.pow(2, retryCount) * 1000 // 1s → 2s → 4s → ... capped at 30s
    }

    delayMs = Math.min(delayMs, 30_000)

    await new Promise((resolve) => setTimeout(resolve, delayMs))
    originalRequest._retryCount429 = retryCount + 1
    return client.request(originalRequest)
  }

  private processQueue = (error: any, token: string | null = null) => {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error)
      } else {
        resolve(token)
      }
    })

    this.failedQueue = []
  }

  private refreshToken = async (options?: {
    skipRedirect?: boolean
    ignoreNavigator?: boolean
  }): Promise<any> => {
    // Share a brief retry window with every queued API request. After exhaustion,
    // avoid starting a new refresh for each polling query while the network is down.
    if (Date.now() < this.refreshRetryAfter) throw this.lastRefreshError
    try {
      const data = await this.refreshTokenOnce(options)
      this.refreshRetryAfter = 0
      return data
    } catch (error) {
      if ((error as { transient?: boolean })?.transient) {
        this.lastRefreshError = error
        this.refreshRetryAfter = Date.now() + 30_000
        // An unknown outcome resolves itself on the next attempt (recovered, re-issued, or a
        // definite rejection that redirects to login), so it reads as reconnecting too.
        markConnectionDegraded()
        console.warn('[ServiceUtil] Session recovery paused', {
          outcomeUnknown: Boolean((error as { outcomeUnknown?: boolean }).outcomeUnknown),
        })
      }
      throw error
    }
  }

  private refreshTokenOnce = async (options?: {
    skipRedirect?: boolean
    ignoreNavigator?: boolean
  }): Promise<any> => {
    // See probeConnection: live backend traffic outranks a stale navigator.onLine.
    if (
      !options?.ignoreNavigator &&
      typeof navigator !== 'undefined' &&
      navigator.onLine === false
    ) {
      throw { status: 503, transient: true, retrySafe: true, message: 'Offline' }
    }
    // A tab left open across a deploy is still running pre-fix, unserialized
    // refresh logic — reload it onto the current bundle instead of letting it
    // race a fresh tab's refresh (see check-build-freshness.ts). Treated as
    // transient, same as a network blip: never logs the user out.
    if (await isRunningStaleBuild()) {
      console.warn('[ServiceUtil] Stale build detected before token refresh — reloading')
      if (typeof window !== 'undefined') {
        window.location.reload()
      }
      markConnectionDegraded()
      return Promise.reject({
        status: 503,
        reason: 'transient',
        transient: true,
        message: 'Reloading to the latest version',
      })
    }

    return withRefreshTokenLock(async () => {
      let browserStorage: Storage | undefined
      try {
        browserStorage = typeof window === 'undefined' ? undefined : window.localStorage
      } catch {}
      this.refreshGuard ??= new RefreshAttemptGuard(
        `orderx-refresh-attempt-${process.env.NEXT_PUBLIC_ENV || 'staging'}`,
        browserStorage
      )
      // Hash the access-token cookie: never persist credentials in the marker.
      // A fresh login or another tab's completed rotation changes this fingerprint.
      let cookieToken = this.getAccessToken() ?? ''
      try {
        const cookie = Cookies.get(COOKIE_KEYS.AUTH_STATE)
        if (cookie) cookieToken = JSON.parse(decodeURIComponent(cookie)).accessToken ?? ''
      } catch {}
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(cookieToken))
      const fingerprint = Array.from(new Uint8Array(digest), (byte) =>
        byte.toString(16).padStart(2, '0')
      ).join('')
      const uncertainError = {
        status: 503,
        reason: 'transient',
        transient: true,
        outcomeUnknown: true,
        message: 'Session refresh outcome could not be confirmed',
      }
      if (this.refreshGuard.isBlocked(fingerprint)) {
        // Read the previous result first: a rotation that landed is replayed from the route's
        // cache without touching the single-use token again.
        const recovery = await fetch('/api/auth/refresh', {
          credentials: 'include',
          cache: 'no-store',
        }).catch(() => null)
        if (!recovery) throw uncertainError
        const recovered = await recovery.json().catch(() => null)
        if (recovery.ok && recovered?.success && recovered.authState?.accessToken) {
          this.refreshGuard.confirm()
          return recovered
        }
        // Nothing to recover (most often the request never left a waking laptop's network,
        // or the cache window passed). Staying blocked would strand the session behind a
        // sign-in banner forever, even across reloads. Send it once more instead: either the
        // token was never used and this succeeds, or the backend rejects the reuse and the
        // auth_rejected branch below sends the user to login — the same place the block led.
        console.warn('[ServiceUtil] Unrecoverable refresh outcome — re-sending once')
      }

      this.refreshGuard.begin(fingerprint)
      try {
        // Never abort or automatically replay this single-use token operation.
        const response = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        })
        const data = await response.json().catch(() => null)
        if (response.ok && data?.success === true && data.authState?.accessToken) {
          this.refreshGuard.confirm()
          return data
        }
        if (data?.reason === 'auth_rejected') {
          this.refreshGuard.confirm()
          if (!options?.skipRedirect && typeof window !== 'undefined') {
            store.set(authAtom, clearAuthState())
            requestLoginRedirect()
          }
          return Promise.reject({
            status: 401,
            reason: 'auth_rejected',
            message: 'Authentication required',
          })
        }
        // The route answered "transient": the backend handed back no tokens, so the cookie
        // still holds a usable refresh token and a plain retry is safe.
        if (data?.reason === 'transient' || data?.retryable === true) {
          this.refreshGuard.confirm()
          throw { ...uncertainError, outcomeUnknown: false, retrySafe: true }
        }
        // Any other 5xx or malformed response cannot prove the backend did not rotate.
        throw uncertainError
      } catch (error) {
        if ((error as { retrySafe?: boolean })?.retrySafe) throw error
        throw uncertainError
      }
    }).catch((error) => {
      // Lock acquisition failed before dispatch (e.g. its wait timeout).
      if (error?.status) throw error
      throw {
        status: 503,
        reason: 'transient',
        transient: true,
        retrySafe: true,
        message: 'Session refresh is busy',
      }
    })
  }

  /**
   * Check JWT token's exp and return expiration time
   */
  private getTokenExpiration(token: string): Date | null {
    const payload = decodeJwtPayload<{ exp?: number }>(token)
    return payload?.exp ? new Date(payload.exp * 1000) : null
  }

  /**
   * Check if token is expired or will expire soon
   * @param bufferSeconds Buffer time before expiration (in seconds, default 60 seconds)
   */
  private isTokenExpiredOrExpiringSoon(token: string, bufferSeconds: number = 60): boolean {
    const expiration = this.getTokenExpiration(token)
    if (!expiration) return true // Consider expired if parsing fails

    const now = new Date()
    const bufferTime = bufferSeconds * 1000 // Convert to milliseconds
    return expiration.getTime() <= now.getTime() + bufferTime
  }

  /**
   * The httpOnly refresh-token cookie is invisible to JS and AUTH_STATE's
   * refreshToken field is always stripped to null, so the client can never
   * check the refresh token directly — the only way to know whether a session
   * is recoverable is to actually ask /api/auth/refresh. This gate exists just
   * to avoid firing that request for visitors with no trace of a session
   * (fresh browser, or post-logout where the profile has been wiped too).
   */
  private hasRecoverableSessionEvidence = (): boolean => {
    if (this.getRefreshToken()) return true
    try {
      const authState = store.get(authAtom)
      if (authState?.user || authState?.accessToken) return true
    } catch {
      // Store unavailable — fall through
    }
    return false
  }

  /**
   * Check if token is valid, and refresh if expired or expiring soon
   * @returns Valid access token (new token after refresh or existing token)
   */
  ensureTokenValid = async (options?: {
    skipRedirect?: boolean
    recoveryProbe?: boolean
    ignoreNavigator?: boolean
  }): Promise<string | null> => {
    // A successful reachability probe may resume immediately after a network return.
    // The rotation guard still prevents replaying an uncertain backend operation.
    if (options?.recoveryProbe) this.refreshRetryAfter = 0
    // Jotai may still cache the value read before another tab/server rotated cookies.
    try {
      const cookie = Cookies.get(COOKIE_KEYS.AUTH_STATE)
      if (cookie) {
        const persisted = JSON.parse(decodeURIComponent(cookie)) as AuthState
        if (persisted.accessToken && persisted.accessToken !== this.getAccessToken()) {
          store.set(authAtom, persisted)
          this.refreshRetryAfter = 0
        }
      }
    } catch {}
    const token = this.getAccessToken()

    // No access token at all: still attempt the refresh as long as some session
    // evidence remains. Bailing out here used to permanently strand sessions
    // whose AUTH_STATE cookie was cleared client-side while a perfectly valid
    // httpOnly refresh-token cookie sat right next to it.
    if (!token && !this.hasRecoverableSessionEvidence()) return null

    // Return token as-is if still valid
    if (token && !this.isTokenExpiredOrExpiringSoon(token)) {
      return token
    }

    // Wait if already refreshing
    if (this.isRefreshing) {
      try {
        const newToken = await new Promise<string | null>((resolve, reject) => {
          this.failedQueue.push({ resolve, reject })
        })
        return newToken
      } catch (refreshError: any) {
        // Mirror the "own refresh" branch below: only a genuine auth rejection
        // from whichever caller is driving this refresh means the session is
        // dead. Collapsing every rejection to null used to force-logout every
        // queued caller on a transient failure (network error, 503) just
        // because someone else happened to own the in-flight refresh.
        const isAuthRejected =
          refreshError?.reason === 'auth_rejected' ||
          refreshError?.status === 401 ||
          refreshError?.response?.status === 401

        if (isAuthRejected) return null
        throw refreshError
      }
    }

    // Start refresh
    this.isRefreshing = true

    try {
      const tokenResponse = await this.refreshToken(options)

      if (!tokenResponse?.success) {
        this.processQueue({ status: 401, message: 'Token refresh failed' }, null)
        return null
      }

      // Update with new tokens
      const currentAuthState = store.get(authAtom)
      const newAccessToken = tokenResponse.authState?.accessToken
      const newRefreshToken = tokenResponse.authState?.refreshToken

      if (newAccessToken) {
        const decodedAccessToken = decodeURIComponent(newAccessToken)
        const decodedRefreshToken = newRefreshToken ? decodeURIComponent(newRefreshToken) : null

        store.set(
          authAtom,
          updateAuthTokens(
            currentAuthState,
            decodedAccessToken,
            decodedRefreshToken ?? currentAuthState.refreshToken
          )
        )

        this.processQueue(null, decodedAccessToken)
        if (!options?.recoveryProbe) store.set(connectionStatusAtom, 'online')
        return decodedAccessToken
      } else {
        const errorResponse = { status: 401, message: 'New access token not found' }
        this.processQueue(errorResponse, null)
        return null
      }
    } catch (refreshError: any) {
      if (!refreshError?.transient) console.error('Token refresh error:', refreshError)
      this.processQueue(refreshError)

      const isAuthRejected =
        refreshError?.reason === 'auth_rejected' ||
        refreshError?.status === 401 ||
        refreshError?.response?.status === 401

      // Only clear tokens on a genuine auth rejection — never on a transient failure.
      if (isAuthRejected) {
        store.set(authAtom, clearAuthState())
        return null
      }

      // Transient: keep the session and let the request fail cleanly so it can retry.
      throw refreshError
    } finally {
      this.isRefreshing = false
    }
  }

  getAccessToken = (): string | null => {
    try {
      const authState = store.get(authAtom)
      if (authState?.accessToken) {
        return authState.accessToken
      }
    } catch (e) {
      // Fallback to cookie if store is not accessible or fails
    }

    if (typeof window === 'undefined') {
      return null
    }

    const authState = Cookies.get(COOKIE_KEYS.AUTH_STATE)
    if (authState) {
      try {
        const parsed = JSON.parse(decodeURIComponent(authState)) as AuthState
        return parsed?.accessToken || null
      } catch (e) {
        return null
      }
    }
    return null
  }

  getRefreshToken = (): string | null => {
    try {
      const authState = store.get(authAtom)
      if (authState?.refreshToken) {
        return authState.refreshToken
      }
    } catch (e) {
      // Fallback
    }

    if (typeof window === 'undefined') {
      return null
    }

    const authState = Cookies.get(COOKIE_KEYS.AUTH_STATE)
    if (authState) {
      try {
        const parsed = JSON.parse(decodeURIComponent(authState)) as AuthState
        return parsed?.refreshToken || null
      } catch (e) {
        return null
      }
    }
    return null
  }

  /**
   * Axios request interceptor handler
   * Attaches access token to the request headers
   */
  handleAxiosRequestInterceptor = <T = any>(
    config: AxiosRequestConfig<T>
  ): AxiosRequestConfig<T> => {
    const token = this.getAccessToken()

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  }

  /**
   * Axios response error interceptor handler
   * Handles 401 unauthorized errors by refreshing the token
   */
  handleAxiosResponseErrorInterceptor = async (
    error: unknown,
    client: AxiosInstance,
    originalRequest?: AxiosRequestConfig
  ): Promise<any> => {
    if (this.isTooManyRequestsError(error)) {
      const axiosError = error as AxiosError<any>
      const config = originalRequest || axiosError.config
      if (config?._skipRateLimitRetry) {
        return Promise.reject(error)
      }
      if (config) {
        return this.handle429Retry(axiosError, client, config)
      }
      return Promise.reject(error)
    }

    if (!this.isUnauthorizedError(error)) {
      return Promise.reject(error)
    }

    const axiosError = error as AxiosError<any>
    const originalRequestConfig = originalRequest || axiosError.config
    const skipRedirect = originalRequestConfig?._skipRedirect

    // Prevent infinite retry loop - if this request was already retried, reject immediately
    if (originalRequestConfig?._retry) {
      return Promise.reject(error)
    }

    // If already refreshing, queue the request
    if (this.isRefreshing) {
      try {
        const token = await new Promise<string | null>((resolve, reject) => {
          this.failedQueue.push({ resolve, reject })
        })

        if (token) {
          if (originalRequestConfig?.headers) {
            originalRequestConfig.headers.Authorization = `Bearer ${token}`
            // Mark as retried to prevent infinite loop
            originalRequestConfig._retry = true
          }
          // Await the retry request to ensure proper response propagation
          const response = await client.request(originalRequestConfig as AxiosRequestConfig)
          return response
        }

        const errorResponse = { status: 401, message: 'Token refresh failed' }
        this.processQueue(errorResponse, null)
        return Promise.reject(errorResponse)
      } catch (queueError) {
        return Promise.reject(queueError)
      }
    }

    this.isRefreshing = true
    let originalRequestWasRetried = false

    try {
      const tokenResponse = await this.refreshToken({ skipRedirect })

      if (!tokenResponse?.success) {
        const errorResponse = { status: 401, message: 'Token refresh failed' }
        this.processQueue(errorResponse, null)
        return Promise.reject(errorResponse)
      }

      // The server automatically updates the cookies, so only update the client state
      const currentAuthState = store.get(authAtom)
      const newAccessToken = tokenResponse.authState?.accessToken
      const newRefreshToken = tokenResponse.authState?.refreshToken

      if (newAccessToken) {
        const decodedAccessToken = decodeURIComponent(newAccessToken)
        const decodedRefreshToken = newRefreshToken ? decodeURIComponent(newRefreshToken) : null

        store.set(
          authAtom,
          updateAuthTokens(
            currentAuthState,
            decodedAccessToken,
            decodedRefreshToken ?? currentAuthState.refreshToken
          )
        )

        this.processQueue(null, decodedAccessToken)
        store.set(connectionStatusAtom, 'online')
        this.isRefreshing = false // Set to false before retry to allow request interceptor to fetch new token from store

        // Create a new request config to avoid interceptor issues
        if (originalRequestConfig?.headers) {
          originalRequestConfig.headers.Authorization = `Bearer ${decodedAccessToken}`
          // Mark as retried to prevent infinite loop
          originalRequestConfig._retry = true
        }

        // Await the retry request to ensure proper response propagation
        originalRequestWasRetried = true
        const response = await client.request(originalRequestConfig as AxiosRequestConfig)
        return response
      } else {
        const errorResponse = { status: 401, message: 'New access token not found' }
        this.processQueue(errorResponse, null)
        return Promise.reject(errorResponse)
      }
    } catch (refreshError: any) {
      // Refresh already succeeded and the endpoint still rejected the retried
      // request. Preserve that endpoint error for the mutation/query caller;
      // it is not evidence that the newly-issued session is invalid.
      if (originalRequestWasRetried) {
        return Promise.reject(refreshError)
      }

      if (!refreshError?.transient) console.error('Token refresh error:', refreshError)
      this.processQueue(refreshError)

      // Clear tokens on refresh failure and force redirect to login
      if (shouldForceLogoutAfterRefreshError(refreshError, originalRequestWasRetried)) {
        store.set(authAtom, clearAuthState())
        if (typeof window !== 'undefined') {
          requestLoginRedirect()
        }
      }

      return Promise.reject(refreshError)
    } finally {
      this.isRefreshing = false
    }
  }

  /**
   * Fetch error handler
   * Handles fetch API errors with 401 unauthorized errors
   */
  handleFetchError = async (
    error: Response | Error | unknown,
    originalFetch?: () => Promise<Response>,
    options?: { skipRedirect?: boolean }
  ): Promise<Response> => {
    let response: Response | null = null

    if (error instanceof Response) {
      response = error
    } else if (error instanceof Error && 'response' in error) {
      // Handle axios-like errors
      response = (error as any).response
    }

    if (response && response.status === 401) {
      // Handle 401 error by refreshing token
      if (this.isRefreshing) {
        try {
          const token = await new Promise<string | null>((resolve, reject) => {
            this.failedQueue.push({ resolve, reject })
          })
          if (token && originalFetch) {
            return await originalFetch()
          }
          throw new Error('Token refreshed but original request info not available to retry.')
        } catch (queueError) {
          throw queueError
        }
      }

      this.isRefreshing = true

      try {
        const tokenResponse = await this.refreshToken(options)

        if (!tokenResponse?.success) {
          const errorResponse = { status: 401, message: 'Token refresh failed' }
          this.processQueue(errorResponse, null)
          throw new Error('Token refresh failed')
        }

        // Update auth state
        const currentAuthState = store.get(authAtom)
        const newAccessToken = tokenResponse.authState?.accessToken
        const newRefreshToken = tokenResponse.authState?.refreshToken

        if (newAccessToken) {
          const decodedAccessToken = decodeURIComponent(newAccessToken)
          const decodedRefreshToken = newRefreshToken ? decodeURIComponent(newRefreshToken) : null

          store.set(
            authAtom,
            updateAuthTokens(
              currentAuthState,
              decodedAccessToken,
              decodedRefreshToken ?? currentAuthState.refreshToken
            )
          )
        }

        // Retry the original request with new token
        const token = this.getAccessToken()
        if (token && originalFetch) {
          this.processQueue(null, token)
          return await originalFetch()
        } else if (token && response instanceof Response) {
          this.processQueue(null, token)
          throw new Error(
            'Token refreshed but original request info not available to retry. Use originalFetch callback.'
          )
        }
      } catch (refreshError: any) {
        this.processQueue(refreshError)

        // Clear tokens on refresh failure
        if (
          refreshError?.status === 401 ||
          refreshError?.response?.status === 401 ||
          refreshError?.message === 'Token refresh failed'
        ) {
          store.set(authAtom, clearAuthState())
        }

        throw refreshError
      } finally {
        this.isRefreshing = false
      }
    }

    // For non-401 errors, re-throw
    if (error instanceof Response) {
      throw error
    }
    throw error
  }
}

// Export singleton instance
export const serviceUtil = ServiceUtil.getInstance()
